// دیکته — a spoken شرح حال turned into a DRAFT the student confirms.
//
// The one AI operation the record screen owns. Everything else the assistant
// does happens inside a visit (the copilot) or on the chat page, and those hooks
// belong to their own screens; this is here because «دیکته» is a way of writing
// the history, and the history is part of the record.
//
// ── A TRANSCRIPT, never audio ─────────────────────────────────────────────
//
// Speech-to-text happens in the BROWSER (`VoicePanel` drives the platform
// recogniser). The BFF's JSON body limit would reject a base64 recording as a
// 413 before any resolver ran — with no error message this app controls — and
// the AI service's streaming transcription RPC is appointment-bound, which this
// app may not dial (core.md §8.1).
//
// ── It writes NOTHING ─────────────────────────────────────────────────────
//
// `parseCaseVoice` returns a draft. The student edits it and confirms what they
// keep through `addCaseHistory` with `source: "voice"` — the same mutation they
// would use by hand (core.md §3.4). There is no state in which an entry belongs
// to the AI rather than to the student; `source` records where the words came
// from and is immutable afterwards.

import React from 'react';

import { bffMutation, errorCode } from '../lib/bffClient.js';
import { PARSE_CASE_VOICE_MUTATION } from '../graphql/documents.js';
import { refreshBalance } from './useMe.js';

/**
 * @returns `{ busy, parseVoice }` — the shape `VoicePanel` reads (`busy` is the
 *          operation name, so a panel with several controls can tell which one
 *          is running; today there is only `'voice'`).
 */
export function useCaseVoice(caseId) {
  const [busy, setBusy] = React.useState(null);

  const parseVoice = React.useCallback(
    async (transcript) => {
      setBusy('voice');
      try {
        const data = await bffMutation(PARSE_CASE_VOICE_MUTATION, { caseId, transcript });
        // The service meters nothing for this today (PARSEH_COSTS.VOICE = 0),
        // but the balance is re-read anyway: the day it starts charging, the
        // number beside the button must move without a client change.
        refreshBalance();
        return data?.parseCaseVoice || null;
      } catch (err) {
        console.error('[useCaseVoice] parse failed:', errorCode(err) || err?.message);
        throw err;
      } finally {
        setBusy(null);
      }
    },
    [caseId],
  );

  return { busy, parseVoice };
}

export default useCaseVoice;
